<template>
  <VContainer fluid>
    <VRow>
      <VCol cols="12">
        <VCard :loading="loading" class="bg-grey-lighten-4 elevation-0">
          <v-toolbar color="primary" density="comfortable">
            <v-toolbar-title class="text-white">
              {{ title }}
            </v-toolbar-title>
            <v-spacer />

            <div class="d-flex ga-2">
              <VTooltip text="Fermer" location="left">
                <template #activator="{ props }">
                  <VBtn v-bind="props" icon variant="text" @click="cancel">
                    <VIcon icon="mdi-close-circle-outline" />
                  </VBtn>
                </template>
              </VTooltip>

              <!-- Save -->
              <VTooltip text="Sauvegarder" location="left">
                <template #activator="{ props }">
                  <VBtn v-bind="props" icon variant="text" @click="save">
                    <VIcon icon="mdi-content-save-all" />
                  </VBtn>
                </template>
              </VTooltip>

              <!-- Remove -->
              <VTooltip v-if="canRemove" text="Supprimer" location="left">
                <template #activator="{ props }">
                  <VBtn v-bind="props" icon variant="text" @click="remove">
                    <VIcon icon="mdi-trash-can-outline" />
                  </VBtn>
                </template>
              </VTooltip>
            </div>
          </v-toolbar>

          <VCardText>
            <!-- Row 1 -->
            <VRow dense>
              <VCol cols="12" md="4">
                <VTextField
                  v-model="avenant.dureeDuContrat"
                  type="number"
                  suffix="ans"
                  class="ms-5"
                  :rules="rules.isNotEmpty"
                >
                  <template #label>
                    Durée du Contrat<span class="text-red"><strong> *</strong></span>
                  </template>
                </VTextField>
              </VCol>

              <VCol cols="12" md="4">
                <VTextField
                  v-model="dateDeDemarrageDisplay"
                  type="date"
                  :rules="rules.date"
                >
                  <template #label>
                    Date de Démarrage<span class="text-red"><strong> *</strong></span>
                  </template>
                </VTextField>
              </VCol>
            </VRow>

            <!-- Row 2: Tarif -->
            <VRow dense>
              <VCol cols="12" md="4">
                <VTextField
                  v-model="avenant.tarifLavage"
                  type="number"
                  prefix="€"
                  label="Tarif Lavage"
                  class="ms-5"
                />
              </VCol>

              <VCol cols="12" md="4">
                <VTextField
                  v-model="avenant.tarifSechage"
                  type="number"
                  prefix="€"
                  label="Tarif Séchage"
                />
              </VCol>

              <VCol cols="12" md="4">
                <VTextField
                  v-model="avenant.tarifLessive"
                  type="number"
                  prefix="€"
                  label="Tarif Lessive"
                  class="me-5"
                />
              </VCol>
            </VRow>

            <!-- Machines: the last amendment holds the new numbers of the contract -->
            <p class="text-subtitle-1 text-primary mx-5 mt-4">Nombre de produits</p>
            <p class="text-body-2 text-medium-emphasis mx-5 mb-2">
              Le dernier avenant remplace les nombres de produits du contrat. Un nouvel avenant
              reprend les nombres actuels.
            </p>
            <VRow dense class="mx-3">
              <VCol v-for="field in machineCountFields" :key="field.key" cols="12" sm="6" md="3">
                <VTextField
                  v-model.number="avenant[field.key]"
                  type="number"
                  min="0"
                  :label="field.label"
                />
              </VCol>
            </VRow>

            <!-- Observation -->
            <VRow dense>
              <VCol cols="12">
                <VTextarea
                  v-model="avenant.observation"
                  label="Observation"
                  class="mx-5"
                />
              </VCol>
            </VRow>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>

    <!-- Confirm Delete Dialog -->
    <ConfirmDialog
      v-model="removeAvenantDialog"
      :dialog-title="dialogTitle"
      :dialog-text="dialogText"
      @confirm="onConfirm"
      @cancel="onCancel"
    />
  </VContainer>
</template>

<script setup lang="ts">
import { reactive, ref, computed, toRef, onMounted } from "vue";
import { useRoute, useRouter } from "vue-router";
import ConfirmDialog from "@/components/ConfirmDialog.vue";
import { useAppStore } from "@/store/app";
import type { Avenant } from "@/types";
import {
  deleteAvenant,
  getAvenantById,
  getAvenantsByLaverie,
  postAvenant,
  putAvenant,
} from "@/utils/avenant-api";
import { getLaverieById } from "@/utils/laverie-api";
import { isNotEmpty, validateDate } from "@/utils/validation-util";
import { useFormattedDate } from "@/composables/useFormattedDate";

/* ---------- Router & Store ---------- */
const route = useRoute();
const router = useRouter();
const appStore = useAppStore();

/* ---------- State ---------- */
const loading = ref(false);
const removeAvenantDialog = ref(false);
const dialogTitle = "Suppression";
const dialogText = "Voulez-vous supprimer cet avenant ?";
const title = ref("");

/* ---------- Form Model ---------- */
const avenant = reactive<Avenant>({} as Avenant);

type MachineCountKey =
  | "nbLaveLinge"
  | "nbLaveLingeChauffant"
  | "nbSecheLinge"
  | "nbStackedLave"
  | "nbStackedSeche"
  | "nbStacked"
  | "nbCentrale"
  | "nbCondensateur"
  | "nbArmoireLessive"
  | "nbPompeLessive"
  | "nbLintTrap"
  | "nbMonnayeur"
  | "nbBallonEauChaude";

const machineCountFields: { key: MachineCountKey; label: string }[] = [
  { key: "nbLaveLinge", label: "Nbre Lave Linge" },
  { key: "nbLaveLingeChauffant", label: "Nbre Lave Linge Chauffant" },
  { key: "nbSecheLinge", label: "Nbre Sèche Linge" },
  { key: "nbStackedLave", label: "Nbre Stacked Lave" },
  { key: "nbStackedSeche", label: "Nbre Stacked Sèche" },
  { key: "nbStacked", label: "Nbre Stacked" },
  { key: "nbCentrale", label: "Nbre Centrale" },
  { key: "nbCondensateur", label: "Nbre Condensateur" },
  { key: "nbArmoireLessive", label: "Nbre Armoire à Lessive" },
  { key: "nbPompeLessive", label: "Nbre Pompe à Lessive" },
  { key: "nbLintTrap", label: "Nbre Lint Trap" },
  { key: "nbMonnayeur", label: "Nbre Monnayeur" },
  { key: "nbBallonEauChaude", label: "Nbre Ballon d'Eau Chaude" },
];

/** A new amendment starts from the current numbers: those of the last amendment, or of the contract. */
async function initializeMachineCounts(laverieId: string) {
  const [laverie, avenants] = await Promise.all([
    getLaverieById(laverieId),
    getAvenantsByLaverie(laverieId),
  ]);
  const lastAvenant = [...avenants].sort(
    (a, b) =>
      new Date(a.dateDeDemarrage).getTime() - new Date(b.dateDeDemarrage).getTime() ||
      Number(a.id) - Number(b.id)
  ).pop();
  const current = lastAvenant ?? laverie;
  for (const field of machineCountFields) {
    avenant[field.key] = current[field.key] ?? 0;
  }
}

/* ---------- Validation ---------- */
const rules = {
  isNotEmpty: [(v: any) => isNotEmpty(v) || "Champ requis"],
  date: [(v: string) => validateDate(v) || "Date invalide"],
};

/* ---------- Date Bridge (maskable) ---------- */
const dateDeDemarrageDisplay = useFormattedDate(toRef(avenant, "dateDeDemarrage"));

/* ---------- Computed ---------- */
const canRemove = computed(() => !!avenant.id);

/* ---------- Actions ---------- */
function cancel() {
  router.push({
    name: "laverie",
    params: { id: route.params.laverieId },
  });
}

function remove() {
  removeAvenantDialog.value = true;
}

function onCancel() {
  removeAvenantDialog.value = false;
}

function isCreatingNewAvenant(): boolean {
  return route.name === "newavenant" || route.params.id === "new";
}

async function onConfirm() {
  try {
    loading.value = true;
    await deleteAvenant(avenant.id!);
    cancel();
    appStore.sendSuccessNotice("Avenant supprimé.");
  } catch {
    appStore.sendErrorNotice("Opération échouée.");
  } finally {
    loading.value = false;
    removeAvenantDialog.value = false;
  }
}

async function save() {
  try {
    loading.value = true;
    if (isCreatingNewAvenant()) {
      const created = await postAvenant(avenant, route.params.laverieId as string);
      Object.assign(avenant, created);

      router.replace({
        name: "avenant",
        params: { laverieId: route.params.laverieId, id: created.id },
      });

      title.value = "Editer l'avenant";
      appStore.sendSuccessNotice("Avenant créé.");
    } else {
      await putAvenant(avenant);
      appStore.sendSuccessNotice("Avenant mis à jour.");
    }
  } finally {
    loading.value = false;
  }
}

/* ---------- Lifecycle ---------- */
onMounted(async () => {
  title.value = isCreatingNewAvenant() ? "Nouveau avenant" : "Editer l'avenant";

  if (!isCreatingNewAvenant()) {
    Object.assign(avenant, await getAvenantById(route.params.id as string));
  } else {
    await initializeMachineCounts(route.params.laverieId as string);
  }
});
</script>

<style scoped>
.title {
  font-size: 1.5rem;
  font-weight: bold;
}
</style>
